import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdReport } from "react-icons/md";

import { useSnack } from "../../../hooks/useSnack";
import classroomServices from "../../../services/classroomServices";
import { getLanguage } from "../../../utils/getLanguage";
import { resetQuestionModalValidationSchema } from "../../../utils/validationSchema";
import Button from "../../Button";
import Modal from "../../Modal";

const ResetLessonQuestionsModal = forwardRef(({ onSubmit, type }, ref) => {
  const { t } = useTranslation();
  const lang = getLanguage();
  const { showSnack } = useSnack();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [classroomID, setClassroomID] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const formik = useFormik({
    initialValues: { selectedLessonQuestion: null },
    validationSchema: resetQuestionModalValidationSchema(t),
    onSubmit: async ({ selectedLessonQuestion }, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const payload = {
          lesson_id: selectedLessonQuestion,
          classroom_id: classroomID,
          students: students.map((s) => s.student.id),
        };

        const response = await classroomServices.resetChallengesOrQuestions("questions", payload);
        showSnack(response.message, "success");

        onSubmit({ selectedLessonQuestion });
        resetForm();
        setIsOpen(false);
      } catch (error) {
        showSnack(error, "error");
      } finally {
        setTimeout(() => setIsSubmitting(false), 500);
      }
    },
  });

  const handleCloseModal = () => {
    formik.resetForm();
    setLessons([]);
    setIsOpen(false);
  };

  useImperativeHandle(ref, () => ({
    openModal(classroomDetails, studentsList) {
      setClassroomID(classroomDetails?.classroom?.id);
      setStudents(studentsList);

      const graduationIds = classroomDetails.graduation_ids || [];
      const availableLessons = (classroomDetails?.lessons || [])
        .filter(
          (lesson) =>
            lesson.is_ordered !== 0 && !lesson.is_locked && !graduationIds.includes(lesson.lesson_id)
        )
        .map((lesson) => ({
          value: lesson.lesson_id,
          label: lang === "ar" ? lesson.title_ar : lesson.title_en,
        }));

      setLessons(availableLessons);
      setIsOpen(true);
    },
    closeModal: handleCloseModal,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-blue-500 dark:text-blue-400">
            <MdReport size={24} /> {t("reset_questions")}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button color="white" variant="outlined" onClick={handleCloseModal} label={t("cancel")} />
          <Button
            className="w-36"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={formik.handleSubmit}
            label={t("reset")}
          />
        </div>
      }
    >
      <div className="space-y-4">
        <p className="font-semibold leading-relaxed text-gray-700 dark:text-gray-300">
          {type === "student" ? t("reset_questions_text_student") : t("reset_questions_text")}
        </p>

        <Dropdown
          pt={{
            wrapper: "text-sm",
            header: "bg-blue-100 dark:bg-gray-700 p-2 rounded-t-md",
            input: "p-0 text-sm placeholder:text-gray-500 dark:text-gray-300 dark:placeholder-gray-500",
            label: "p-0 text-sm placeholder:text-gray-500 dark:text-gray-300 dark:placeholder-gray-500",
            root: "w-full rounded-md border bg-gray-100 px-3 py-2 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 dark:border-gray-700 dark:bg-gray-800",
            panel:
              "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
            item: "p-2 hover:bg-blue-100 dark:hover:bg-blue-700",
          }}
          value={formik.values.selectedLessonQuestion}
          onChange={(e) => formik.setFieldValue("selectedLessonQuestion", e.value)}
          options={lessons}
          optionLabel="label"
          optionValue="value"
          placeholder={t("select_lesson")}
          showClear
          emptyFilterMessage={t("no_results_found")}
          display="chip"
          filter
        />

        {formik.errors.selectedLessonQuestion && formik.touched.selectedLessonQuestion && (
          <p className="text-sm text-red-500">{formik.errors.selectedLessonQuestion}</p>
        )}
      </div>
    </Modal>
  );
});

export default ResetLessonQuestionsModal;
